import { createReducer } from '@reduxjs/toolkit'

import { FeatureKey } from '@/constants/feature-key'
import { Repeat } from '@/constants/repeat-options'
import { Shift } from '@/model/shift'
import { ShiftDuration } from '@/model/shift-duration'
import { ShiftSchedule } from '@/model/shift-schedule'
import { axios } from '@/utils/axios'
import { removeItem, updateOrAddArray, updateOrAddArrayItem } from '@/utils/draft'

import { createAppAsyncThunk } from './utils'

const namespace = 'shift'

export type CreateShiftParams = Omit<Shift, 'id' | 'organizationId'>
export const createShift = createAppAsyncThunk(
  `${namespace}/createShift`,
  async (params: CreateShiftParams) => (await axios.post<Shift>(
    '/shift',
    {
      ...params,
    },
  )).data,
)

export type UpdateShiftParams = {
  id?: number
  startAt?: string
  endAt?: string
  creatorIds?: number[]
  employeeIds?: number[]
  repeat?: Repeat
  repeatEndAt?: string
  repeatWeekDays?: number[]
  shiftScheduleId?: number
  isAllFuture?: boolean
}

export const updateShift = createAppAsyncThunk(
  `${namespace}/updateShift`,
  async ({ id, ...rest }: UpdateShiftParams) => (await axios.put<Shift>(
    `/shift/${id}`,
    {
      id,
      ...rest,
    },
  )).data,
)

export const readAllShiftDurations = createAppAsyncThunk(
  `${namespace}/readAllShiftDurations`,
  async () => (await axios.get<ShiftDuration[]>(
    '/shift-duration/all',
  )).data,
)

export type CreateShiftDurationParams = Omit<ShiftDuration, 'id' | 'organizationId'>
export const createShiftDuration = createAppAsyncThunk(
  `${namespace}/createShiftDuration`,
  async (
    { startAt, endAt }: CreateShiftDurationParams,
    { getState },
  ) => {
    const { global: { permissionKey } } = getState()
    const { data } = await axios.post<ShiftDuration>(
      '/shift-duration',
      {
        startAt,
        endAt,
        permissionKey,
      },
    )
    return data
  },
)

export type UpdateShiftDurationParams = Omit<ShiftDuration, 'organizationId'>
export const updateShiftDuration = createAppAsyncThunk(
  `${namespace}/updateShiftDuration`,
  async ({ id, startAt, endAt }: UpdateShiftDurationParams) => (await axios.put<ShiftDuration>(
    `/shift-duration/${id}`,
    {
      id,
      startAt,
      endAt,
    },
  )).data,
)

export const removeShiftDuration = createAppAsyncThunk(
  `${namespace}/removeShiftDuration`,
  async (id: number) => (await axios.delete(`/shift-duration/${id}`)),
)

export type ReadAllShifSchedulesParams = {
  employeeGroupId?: number
  employeeId?: number
  creatorId?: number
  startAt: string
  endAt: string
  permissionKey?: FeatureKey
}

export const readAllShiftSchedules = createAppAsyncThunk(
  `${namespace}/readAllShiftSchedules`,
  async ({
    employeeGroupId,
    employeeId,
    creatorId,
    startAt,
    endAt,
  }: ReadAllShifSchedulesParams,
    { getState },
  ) => {
    const { global: { permissionKey } } = getState()
    const { data } = await axios.get<[ShiftSchedule]>(
      '/shift/schedule/all', {
      params: {
        employeeGroupId,
        employeeId,
        creatorId,
        startAt: startAt,
        endAt: endAt,
        permissionKey,
      },
    })
    return data
  },
)

export const readMyShiftSchedules = createAppAsyncThunk(
  `${namespace}/readMyShiftSchedules`,
  async ({
    startAt,
    endAt,
  }: { startAt: Date; endAt: Date },
    { getState },
  ) => {
    const { global: { permissionKey } } = getState()
    const { data } = await axios.get<[ShiftSchedule]>(
      '/shift/schedule/mine', {
      params: {
        startAt,
        endAt,
        permissionKey,
      },
    })
    return data
  },
)

export type WorkedHours = {
  employeeId: number
  employeeName: string
  minutes: number
}
export const readShiftWorkedHours = createAppAsyncThunk(
  `${namespace}/readShiftWorkedHours`,
  async (
    {
      startAt,
      endAt,
    }: { startAt: Date; endAt: Date },
    { getState },
  ) => {
    const { global: { permissionKey } } = getState()
    const { data } = await axios.get<[WorkedHours]>(
      '/shift/worked-hours', {
      params: {
        startAt,
        endAt,
        permissionKey,
      },
    })
    return data
  },
)

export const removeShiftSchedule = createAppAsyncThunk(
  `${namespace}/removeShiftSchedule`,
  async (
    { id, isAllFuture }: { id: number; isAllFuture?: boolean },
    { getState },
  ) => {
    const { global: { permissionKey } } = getState()
    await axios.delete(`/shift/schedule/${id}`, {
      params: {
        isAllFuture,
        permissionKey,
      },
    })
  },
)

const initialState: {
  shiftDurations: ShiftDuration[]
  shiftSchedules: ShiftSchedule[]
} = {
  shiftDurations: [],
  shiftSchedules: [],
}

const reducer = createReducer(initialState, (builder) => {
  builder.addCase(readAllShiftDurations.fulfilled, (draft, { payload }) => {
    draft.shiftDurations = payload
  })
  builder.addCase(createShiftDuration.fulfilled, (draft, { payload }) => {
    draft.shiftDurations.push(payload)
  })
  builder.addCase(updateShiftDuration.fulfilled, (draft, { payload }) => {
    updateOrAddArrayItem(payload, draft.shiftDurations)
  })
  builder.addCase(removeShiftDuration.fulfilled, (draft, { meta }) => {
    removeItem(meta.arg, draft.shiftDurations)
  })
  builder.addCase(readAllShiftSchedules.fulfilled, (draft, { payload }) => {
    updateOrAddArray(payload, draft.shiftSchedules)
  })
  builder.addCase(removeShiftSchedule.fulfilled, (draft, { meta }) => {
    removeItem(meta.arg.id, draft.shiftSchedules)
  })
})

export default { [namespace]: reducer }
